import React, { useId } from "react"
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from "recharts"

export interface TrendPoint {
  label: string   // "Oct 1"
  current: number
  previous: number
}

interface TooltipPayloadEntry {
  name: string
  value: number
  color: string
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string
}) {
  if (!active || !payload?.length) return null
  const current  = payload.find((p) => p.name === "current")
  const previous = payload.find((p) => p.name === "previous")
  return (
    <div className="rounded-lg border border-border bg-card shadow-lg px-3.5 py-2.5 text-xs min-w-[160px]">
      <p className="font-semibold text-foreground mb-2 text-[11px]">{label}</p>
      {current && (
        <div className="flex items-center justify-between gap-4 mb-1">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block w-3 h-[2px] rounded-full bg-primary" />
            This period
          </span>
          <span className="font-semibold text-foreground tabular-nums">{current.value}</span>
        </div>
      )}
      {previous && (
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span
              className="inline-block w-3 h-[1.5px] rounded-full bg-zinc-400"
              style={{ backgroundImage: "repeating-linear-gradient(90deg,#a1a1aa 0,#a1a1aa 3px,transparent 3px,transparent 6px)" }}
            />
            Prior period
          </span>
          <span className="font-medium text-muted-foreground tabular-nums">{previous.value}</span>
        </div>
      )}
    </div>
  )
}

function CustomCursor({ x, y, height }: { x?: number; y?: number; height?: number }) {
  if (x == null || y == null || height == null) return null
  return (
    <line
      x1={x} y1={y} x2={x} y2={y + height}
      stroke="#a1a1aa"
      strokeWidth={1}
      strokeDasharray="4 3"
    />
  )
}

const readPrimary = (): string => {
  if (typeof window === "undefined") return "#6366f1"
  const tmp = document.createElement("div")
  tmp.style.cssText = "position:absolute;visibility:hidden;color:hsl(var(--primary,222 47% 55%))"
  document.body.appendChild(tmp)
  const color = getComputedStyle(tmp).color
  document.body.removeChild(tmp)
  return color || "#6366f1"
}

export function TrendCompareChart({ data }: { data: TrendPoint[] }) {
  const gradId = useId().replace(/:/g, "")
  const primary = readPrimary()

  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")

  const gridStroke = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
  const tickFill   = isDark ? "#71717a" : "#a1a1aa"
  const prevStroke = isDark ? "#52525b" : "#d4d4d8"

  // Show only first, middle, last date labels
  const tickIndices = new Set([0, Math.floor(data.length / 2), data.length - 1])

  return (
    <ResponsiveContainer width="100%" height={120}>
      <ComposedChart data={data} margin={{ top: 6, right: 4, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`tg-${gradId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={primary} stopOpacity={isDark ? 0.3 : 0.2} />
            <stop offset="80%"  stopColor={primary} stopOpacity={0.03} />
            <stop offset="100%" stopColor={primary} stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="0" horizontal vertical={false} stroke={gridStroke} />

        <XAxis
          dataKey="label"
          axisLine={false}
          tickLine={false}
          tick={({ x, y, index, payload }) =>
            tickIndices.has(index) ? (
              <text
                x={x} y={y + 10}
                textAnchor={index === 0 ? "start" : index === data.length - 1 ? "end" : "middle"}
                fill={tickFill}
                fontSize={10}
                fontFamily="system-ui, sans-serif"
              >
                {payload.value}
              </text>
            ) : null
          }
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          width={28}
          tick={{ fill: tickFill, fontSize: 10, fontFamily: "system-ui, sans-serif" }}
          tickFormatter={(v: number) =>
            v === 0 ? "0" : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`
          }
          tickCount={4}
          allowDecimals={false}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={<CustomCursor />}
          wrapperStyle={{ outline: "none" }}
        />

        {/* Previous period — dashed grey line, no fill */}
        <Line
          type="monotone"
          dataKey="previous"
          name="previous"
          stroke={prevStroke}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          dot={false}
          activeDot={false}
          isAnimationActive={false}
        />

        {/* Current period — solid colored area */}
        <Area
          type="monotone"
          dataKey="current"
          name="current"
          stroke={primary}
          strokeWidth={2}
          fill={`url(#tg-${gradId})`}
          dot={false}
          activeDot={{ r: 4, fill: primary, stroke: isDark ? "#18181b" : "#fff", strokeWidth: 2 }}
          isAnimationActive
          animationDuration={500}
          animationEasing="ease-out"
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
